import React, { Fragment } from "react";
import { TooltipHeader, TooltipBody } from "./Tooltip.styles";
import Link from "next/link";

export type TooltipName = keyof typeof Tooltips;

export const Tooltips = {
  goldMember: () => (
    <Fragment>
      <TooltipHeader>Gold Members Only</TooltipHeader>
      <TooltipBody>
        Attend more shows to unlock the ability to book more tickets
      </TooltipBody>
    </Fragment>
  ),
  leadBooker: () => (
    <Fragment>
      <TooltipHeader>What is LB?</TooltipHeader>
      <TooltipBody>
        LB stands for 'Lead Booker'. This is your ticket request and our key
        contact.
      </TooltipBody>
    </Fragment>
  ),
  ageLimit: () => (
    <Fragment>
      <TooltipHeader>Age Limit</TooltipHeader>
      <TooltipBody>
        This guest is under 18 and will need to be accompanied to the show by a
        parent of guardian.
      </TooltipBody>
    </Fragment>
  ),
  underAgeLimit: () => (
    <Fragment>
      <TooltipHeader>Minimum Age Limit</TooltipHeader>
      <TooltipBody>
        This guest is under minimum show age limit and will need to be accompanied to the show by a
        parent of guardian.
      </TooltipBody>
    </Fragment>
  ),
  generalAdmission: () => (
    <Fragment>
      <TooltipHeader>General Admission</TooltipHeader>
      <TooltipBody>
        General Admission refers to a ticketing category where attendees have
        access to the event venue without specific seat assignments.
      </TooltipBody>
    </Fragment>
  ),
  accessibilityRequired: () => (
    <Fragment>
      <TooltipHeader>Accessibility Required Admission</TooltipHeader>
      <TooltipBody>
        Accessibility Required Admission is designed to ensure inclusivity for
        individuals with specific needs or disabilities. This ticket category
        provides accommodations to enhance accessibility, such as
        wheelchair-accessible seating, assistance services, or any other
        features that facilitate a comfortable and enjoyable experience for
        everyone.
      </TooltipBody>
    </Fragment>
  ),
  vip: () => (
    <Fragment>
      <TooltipHeader>
        <em>VIP</em> Admission
      </TooltipHeader>
      <TooltipBody>
        VIP Admission is a premium ticket category that grants attendees
        exclusive privileges and enhanced experiences at an session.
      </TooltipBody>
    </Fragment>
  ),
  friendsAndFamily: () => (
    <Fragment>
      <TooltipHeader>
        <em>Friends and Family</em> Admission
      </TooltipHeader>
      <TooltipBody>
        Friends and Family Admission is category of tickets or entry reserved
        for individuals closely connected to the organizers, performers, or
        participants.
      </TooltipBody>
    </Fragment>
  ),
  generic: () => (
    <Fragment>
      <TooltipHeader>Generic Allocation</TooltipHeader>
      <TooltipBody>
        A placeholder allocation. Specific types like <strong>VIP</strong>,{" "}
        <strong>Friends and Family</strong>, or <strong>Priority</strong> will
        be assigned later.
      </TooltipBody>
    </Fragment>
  ),
  correctEmail: () => (
    <Fragment>
      <TooltipHeader>Don't be 'That Guy'</TooltipHeader>
      <TooltipBody>
        We know it's tempting to send your guest's tickets to another email
        address of your own. Please don't. Everyone 18 and above needs their own
        verified account, complete with a unique email AND mobile number. Legal
        obligations demand that we have the deets of every studio guest. Guests
        under 18 will have their ticket sent to the legal guardian that you have
        selected.
      </TooltipBody>
    </Fragment>
  ),
  manageBooking: () => (
    <Fragment>
      <TooltipHeader>Manage Your Booking</TooltipHeader>
      <TooltipBody>
        Feel like switching up your entourage? Got a mate's email wrong? No
        worries! You can remove or change your guests details up until the point
        that they accept their ticket offer. If you've got additional pals
        itching to join the party, they will need to apply separately. Each
        member can only submit one ticket request per session.
      </TooltipBody>
    </Fragment>
  ),
  lastMinuteTickets: () => (
    <Fragment>
      <TooltipHeader>Last Minute Tickets </TooltipHeader>
      <TooltipBody>
        People's plans do change. As soon as a ticket is declined, our system
        will send out a ticket offer to the next person on the list. This means
        you could still receive a ticket offer up until the day of the show!
      </TooltipBody>
    </Fragment>
  ),
  ticketOfferExpiration: () => (
    <Fragment>
      <TooltipHeader>Going, Going, Gone</TooltipHeader>
      <TooltipBody>
        Keep a sharp eye on your email because once that ticket offer lands,
        you've only got a <b>48 hours</b> to accept your ticket before the offer
        is cancelled and the tickets are sent to the next person in line.
      </TooltipBody>
    </Fragment>
  ),
  showMustGoOn: () => (
    <Fragment>
      <TooltipHeader>The Show Must Go On</TooltipHeader>
      <TooltipBody>
        Tickets are FREE... often people don't show up. We do over-ticket our
        shows to make sure we have a full audience. Our SMS Check-In should
        prevent turn-aways but sometimes studio seating capacities may change on
        show day without our knowledge.
      </TooltipBody>
    </Fragment>
  ),
  editLocked: () => (
    <Fragment>
      <TooltipHeader>Trying To Apply On Behalf Of Someone Else? </TooltipHeader>
      <TooltipBody>
        This is YOUR account and therefore YOUR ticket as Lead Booker. If you
        are not planning to attend, you cannot request a ticket on behalf of
        anyone else. Every Audience Member over 18 must have their own verified
        account to accept a ticket. This is a legal requirement.
      </TooltipBody>
    </Fragment>
  ),
  whatIfImUnderAge: () => (
    <Fragment>
      <TooltipHeader>What if I'm under 18?</TooltipHeader>
      <TooltipBody>
        If you are under Under 18 you must be accompanied by a Parent or
        Guardian and they will need to book the tickets. You can still register
        to our{" "}
        <Link
          href="https://confirmsubscription.com/h/d/A9453CAB44C3AAC6"
          target="_blank"
          rel="noopener noreferrer"
        >
          Mailing List
        </Link>{" "}
        to know what's coming up though!
      </TooltipBody>
    </Fragment>
  ),
  appearanceReleaseAndNDA: () => (
    <Fragment>
      <TooltipHeader>
        Appearance Release & Non Disclosure Agreement
      </TooltipHeader>
      <TooltipBody>
        This is a legally binding contract between yourself and the Production
        Company. You agree to be filmed and that you won't spill the beans on
        what you've witnessed on a show before it goes to air.
      </TooltipBody>
    </Fragment>
  ),
  profileEmailLock: () => (
    <Fragment>
      <TooltipHeader>Want To Change Your Email ?</TooltipHeader>
      <TooltipBody>
        Currently under construction. Soon you will be able to send a new
        verification link and replace the email attached to your account. In the
        meantime, please email{" "}
        <Link
          href="mailto:audience@thatstheticket.com.au"
          target="_blank"
          rel="noopener noreferrer"
        >
          audience@thatstheticket.com.au
        </Link>{" "}
        with any changes needed.
      </TooltipBody>
    </Fragment>
  ),
  profileFirstNameLock: () => (
    <Fragment>
      <TooltipHeader>Want to Change Your First Name?</TooltipHeader>
      <TooltipBody>
        Please remember this is the name that will appear on your future tickets
        and must match you photo ID. Please email{" "}
        <Link
          href="mailto:audience@thatstheticket.com.au"
          target="_blank"
          rel="noopener noreferrer"
        >
          audience@thatstheticket.com.au
        </Link>{" "}
        with your name change request.
      </TooltipBody>
    </Fragment>
  ),
  profileLastNameLock: () => (
    <Fragment>
      <TooltipHeader>Want to Change Your Last Name?</TooltipHeader>
      <TooltipBody>
        Please remember this is the name that will appear on your ticket and
        must match you photo ID. Please email{" "}
        <Link
          href="mailto:audience@thatstheticket.com.au"
          target="_blank"
          rel="noopener noreferrer"
        >
          audience@thatstheticket.com.au
        </Link>{" "}
        with your name change request.
      </TooltipBody>
    </Fragment>
  ),
  profileMobileNumberLock: () => (
    <Fragment>
      <TooltipHeader>Want To Change Your Mobile Number?</TooltipHeader>
      <TooltipBody>
        Currently under construction. Soon you will be able to send a new
        verification link and replace the mobile number attached to your
        account. In the meantime, please email{" "}
        <Link
          href="mailto:audience@thatstheticket.com.au"
          target="_blank"
          rel="noopener noreferrer"
        >
          audience@thatstheticket.com.au
        </Link>{" "}
        with any changes needed.
      </TooltipBody>
    </Fragment>
  ),
  groupTicket: () => (
    <Fragment>
      <TooltipHeader>What is GT?</TooltipHeader>
        <TooltipBody>
        GT stands for 'Group Ticket'. This ticket contains a substanial number of bookings.
        </TooltipBody>
    </Fragment>
  ),
  priority: () => (
    <Fragment>
      <TooltipHeader>
        Priority Ticket
      </TooltipHeader>
    </Fragment>
  ),
  acceptTerms: () => (
    <Fragment>
      <TooltipHeader>Accepting Show's Ts & Cs</TooltipHeader>
      <TooltipBody>
        By proceeding, you confirm that you have read and agree to our Terms and Conditions. Please take a moment to review our policies before you continue.
      </TooltipBody>
    </Fragment>
  ),
  allocationAccessRequirementsLeadBooker: () => (
    <Fragment>
      <TooltipBody>Either the Recipient or one of their guests has Access Requirements</TooltipBody>
    </Fragment>
  ),
  allocationGroupTicket: () => (
    <Fragment>
      <TooltipBody>This is a Group Ticket ({">"}6 guests)</TooltipBody>
    </Fragment>
  ),
  allocationAccessRequirementsGuest: () => (
    <Fragment>
      <TooltipBody>This Recipient has Access Requirements</TooltipBody>
    </Fragment>
  ),
  allocationUnderMinimumAgeGuest: () => (
    <Fragment>
      <TooltipBody>This Recipient is under the show’s minimum age limit and requires approval before ticket can be confirmed</TooltipBody>
    </Fragment>
  ),
  allocationUnderMinimumAgeGuestLeadBooker: () => (
    <Fragment>
      <TooltipBody>One of this Recipient’s guests is under the show’s minimum age limit and requires approval before ticket can be confirmed</TooltipBody>
    </Fragment>
  ),
  allocationUnderAgeGuest: () => (
    <Fragment>
      <TooltipBody>This Recipient is under 18 but above the show’s minimum age limit</TooltipBody>
  </Fragment>
  ),
  weekendDeadline: () => (
    <Fragment>
      <TooltipHeader>Weekend Deadline</TooltipHeader>
      <TooltipBody>
        This deadline falls on a weekend and may be missed by the allocation manager if they aren’t at work. Just a heads up.
      </TooltipBody>
    </Fragment>
  ),
};
